"use client";

import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { Cropper, type CropperRef } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";
import { formatBytes } from "@/lib/tool-utils";
const MAX_FILE_BYTES = 30 * 1024 * 1024; // 30 MB
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const DEFAULT_STATUS = "Upload an image to crop it locally in your browser.";
const ASPECTS: {
  label: string;
  value: number | undefined;
}[] = [{
  label: "Free",
  value: undefined
}, {
  label: "1:1",
  value: 1
}, {
  label: "16:9",
  value: 16 / 9
}, {
  label: "4:3",
  value: 4 / 3
}, {
  label: "3:4",
  value: 3 / 4
}, {
  label: "9:16",
  value: 9 / 16
}];
type OutputFormat = "image/png" | "image/jpeg" | "image/webp";
const FORMAT_LABELS: Record<OutputFormat, string> = {
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/webp": "WebP"
};
const FORMAT_EXTENSIONS: Record<OutputFormat, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp"
};

/** Pick a sensible default output format from the uploaded file. */
function defaultFormat(type: string): OutputFormat {
  if (type === "image/jpeg") return "image/jpeg";
  if (type === "image/webp") return "image/webp";
  return "image/png"; // PNG and GIF keep transparency
}
function stemOf(name: string) {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}
export default function ImageCropper({}: {
  toolName: string;
}) {
  const cropperRef = useRef<CropperRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const sourceUrlRef = useRef("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const [format, setFormat] = useState<OutputFormat>("image/png");
  const [quality, setQuality] = useState(0.92);
  const [rotation, setRotation] = useState(0); // always kept in -180..180
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [cropperFailed, setCropperFailed] = useState(false);
  const [outputSize, setOutputSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Track the rotation the cropper actually has, so slider and buttons never drift.
  const appliedRotation = useRef(0);

  // Free the source image URL on unmount.
  useEffect(() => {
    return () => {
      if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    };
  }, []);
  const loadFile = useCallback((file: File) => {
    setError("");
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Please choose a PNG, JPG, WebP or GIF image.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError(`That image is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_FILE_BYTES)}.`);
      return;
    }
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    const url = URL.createObjectURL(file);
    sourceUrlRef.current = url;
    appliedRotation.current = 0;
    setRotation(0);
    setAspect(undefined);
    setCropperFailed(false);
    setOutputSize(null);
    setFormat(defaultFormat(file.type));
    setFileName(file.name);
    setSourceUrl(url);
    setStatus(file.type === "image/gif" ? `Loaded ${file.name}. Animated GIFs are cropped as a single still frame.` : `Loaded ${file.name} (${formatBytes(file.size)}).`);
  }, []);
  function clearImage() {
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    sourceUrlRef.current = "";
    appliedRotation.current = 0;
    setSourceUrl("");
    setFileName("");
    setRotation(0);
    setAspect(undefined);
    setCropperFailed(false);
    setOutputSize(null);
    setError("");
    setStatus(DEFAULT_STATUS);
  }

  /* --------------------------- Cropper controls --------------------------- */

  function setRotationTo(next: number) {
    // Normalize into -180..180 so the slider always reflects reality.
    const normalized = ((next + 180) % 360 + 360) % 360 - 180;
    const delta = normalized - appliedRotation.current;
    // Take the shortest way round so a 179 -> -179 jump is a 2 degree turn.
    const shortest = ((delta + 180) % 360 + 360) % 360 - 180;
    if (shortest !== 0) cropperRef.current?.rotateImage(shortest);
    appliedRotation.current = normalized;
    setRotation(normalized);
  }
  function resetAll() {
    cropperRef.current?.reset();
    appliedRotation.current = 0;
    setRotation(0);
    setAspect(undefined);
    setError("");
    setStatus("Reset. The image is back to its original position.");
  }

  /* -------------------------------- Export -------------------------------- */

  async function exportCrop() {
    const cropper = cropperRef.current;
    if (!cropper || !sourceUrl) {
      setError("Upload an image first.");
      return;
    }
    setIsExporting(true);
    setError("");
    try {
      const canvas = cropper.getCanvas({
        // Solid white behind JPEG, since JPEG can't store transparency.
        fillColor: format === "image/jpeg" ? "#ffffff" : undefined
      });
      if (!canvas) throw new Error("Could not read the crop area. Try moving the crop box and retry.");
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, format, format === "image/png" ? undefined : quality));
      if (!blob) throw new Error("Your browser could not create this image. Try a different format.");
      const downloadName = `${stemOf(fileName)}-cropped.${FORMAT_EXTENSIONS[format]}`;
      const url_0 = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url_0;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Give the browser a moment to start the download before freeing the URL.
      window.setTimeout(() => URL.revokeObjectURL(url_0), 5000);
      setOutputSize({
        width: canvas.width,
        height: canvas.height
      });
      setStatus(`Downloaded ${downloadName} (${canvas.width}×${canvas.height}px, ${formatBytes(blob.size)}).`);
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : "Something went wrong while cropping.");
    } finally {
      setIsExporting(false);
    }
  }

  /* --------------------------------- Drop --------------------------------- */

  const onDragEnter = (event: DragEvent) => {
    event.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const onDrop = (event_0: DragEvent) => {
    event_0.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    const file_0 = event_0.dataTransfer.files?.[0];
    if (file_0) loadFile(file_0);
  };
  const hasImage = Boolean(sourceUrl) && !cropperFailed;
  const pill = (active: boolean) => `rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`;
  return <div className="space-y-4">
      {!hasImage && <div role="button" tabIndex={0} aria-label="Choose an image to crop, or drag one here" onClick={() => fileInputRef.current?.click()} onKeyDown={event_1 => {
      if (event_1.key === "Enter" || event_1.key === " ") {
        event_1.preventDefault();
        fileInputRef.current?.click();
      }
    }} onDragEnter={onDragEnter} onDragOver={event_2 => event_2.preventDefault()} onDragLeave={onDragLeave} onDrop={onDrop} className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-indigo-300 ${isDragging ? "border-indigo-500 bg-indigo-50" : "border-slate-300 bg-slate-50 hover:border-indigo-400"}`}>
          <p className="text-base font-semibold text-slate-900">
            {isDragging ? "Drop your image" : "Drop an image here, or click to choose"}
          </p>
          <p className="mt-2 text-sm text-slate-600">
            PNG, JPG, WebP or GIF, up to {formatBytes(MAX_FILE_BYTES)}. It never leaves your device.
          </p>
          <span className="mt-4 inline-block rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">
            Choose image
          </span>
        </div>}

      <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={event_3 => {
      const file_1 = event_3.target.files?.[0];
      if (file_1) loadFile(file_1);
      event_3.target.value = "";
    }} />

      {error && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>}

      {cropperFailed && <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-rose-300 bg-rose-50 p-6 text-center text-sm text-rose-700">
          <p className="font-semibold">This image couldn&apos;t be opened.</p>
          <p className="mt-1">It may be corrupted, or in a format your browser can&apos;t display (such as HEIC).</p>
          <button type="button" onClick={clearImage} className="mt-4 rounded-lg bg-rose-100 px-4 py-2 font-semibold hover:bg-rose-200">
            Choose a different image
          </button>
        </div>}

      {hasImage && <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <Cropper key={sourceUrl} ref={cropperRef} src={sourceUrl} className="h-[320px] w-full sm:h-[420px]" stencilProps={{
            aspectRatio: aspect
          }} onError={() => setCropperFailed(true)} />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Drag the box to move it, drag its edges to resize, and scroll or pinch to zoom.
            </p>
          </div>

          <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Aspect ratio</p>
              <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Aspect ratio">
                {ASPECTS.map(option => <button key={option.label} type="button" aria-pressed={aspect === option.value} onClick={() => setAspect(option.value)} className={pill(aspect === option.value)}>
                    {option.label}
                  </button>)}
              </div>
            </div>

            <label className="block text-sm font-medium text-slate-700">
              Rotation: <span className="font-normal text-slate-500">{rotation}°</span>
              <input type="range" min="-180" max="180" step="1" value={rotation} onChange={event_4 => setRotationTo(Number(event_4.target.value))} className="mt-2 w-full" />
            </label>

            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setRotationTo(rotation - 90)} className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200">
                ↺ Rotate left
              </button>
              <button type="button" onClick={() => setRotationTo(rotation + 90)} className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800">
                ↻ Rotate right
              </button>
              <button type="button" onClick={() => cropperRef.current?.flipImage(true, false)} className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200">
                ⇋ Flip horizontal
              </button>
              <button type="button" onClick={() => cropperRef.current?.flipImage(false, true)} className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200">
                ⇅ Flip vertical
              </button>
            </div>

            <div>
              <p className="text-sm font-medium text-slate-700">Save as</p>
              <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Output format">
                {(Object.keys(FORMAT_LABELS) as OutputFormat[]).map(value => <button key={value} type="button" aria-pressed={format === value} onClick={() => setFormat(value)} className={pill(format === value)}>
                    {FORMAT_LABELS[value]}
                  </button>)}
              </div>

              {format !== "image/png" && <label className="mt-3 block text-xs font-medium text-slate-600">
                  Quality: {Math.round(quality * 100)}%
                  <input type="range" min="0.5" max="1" step="0.01" value={quality} onChange={event_5 => setQuality(Number(event_5.target.value))} className="mt-1 w-full" />
                </label>}
              {format === "image/jpeg" && <p className="mt-1 text-xs text-slate-500">
                  JPG has no transparency, so empty areas are filled with white.
                </p>}
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <button type="button" onClick={() => void exportCrop()} disabled={isExporting} className="flex-1 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300">
                {isExporting ? "Preparing…" : "Crop & download"}
              </button>
              <button type="button" onClick={resetAll} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Reset
              </button>
            </div>

            <button type="button" onClick={clearImage} className="w-full rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-100">
              Choose a different image
            </button>

            {outputSize && <p className="text-xs text-slate-500">
                Last export: {outputSize.width}×{outputSize.height}px
              </p>}
          </div>
        </div>}

      <p aria-live="polite" className="inline-block rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
        {status}
      </p>
    </div>;
}